import type { NavigateFunction } from "react-router-dom";

import { buildAuthenticatedUrlDeeplink } from "../../utils/urlUtils";
import { openDeeplink } from "./utils";

export type TreatmentActionLinkNavigation =
  | { type: "internal"; path: string }
  | { type: "deeplink"; url: string }
  | { type: "external"; url: string };

/**
 * Action-link destinations use the native navigation behavior appropriate
 * for their URL:
 *
 * - Existing custom-scheme and Noom universal deeplinks remain unchanged.
 * - Noom-owned web URLs open in a fresh authenticated push webview.
 * - Other same-host URLs remain SPA-routable.
 * - Unknown external URLs remain external.
 */
const isInternalHostname = (hostname: string): boolean => {
  const selfHostname = window.location.hostname;
  return hostname === selfHostname || hostname.endsWith(`.${selfHostname}`);
};

// Mirrors the native `inAppLinkDomains` Firebase parameter. iOS silently
// declines AUTHENTICATED_URL targets outside this allowlist.
const IN_APP_LINK_DOMAINS = ["noom.com", "wsli.dev"];

const isAuthenticatedWebviewHostname = (hostname: string): boolean =>
  IN_APP_LINK_DOMAINS.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
  );

const isNoomUniversalDeeplink = (url: URL): boolean =>
  (url.hostname === "link.noom.com" ||
    url.hostname === "link-test.noom.com") &&
  url.pathname === "/app" &&
  url.searchParams.has("open");

const toInternalPath = (url: URL): string => `${url.pathname}${url.search}`;

/**
 * Resolves how to open a treatment detail action link inside healthcare-client,
 * an external browser, or the native app (noom://).
 */
export const resolveTreatmentActionLinkNavigation = (
  url: string,
  title?: string
): TreatmentActionLinkNavigation => {
  const trimmed = url.trim();

  if (trimmed.startsWith("noom://") || trimmed.startsWith("noomtest://")) {
    return { type: "deeplink", url: trimmed };
  }

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      const parsed = new URL(trimmed);
      if (isNoomUniversalDeeplink(parsed)) {
        return { type: "deeplink", url: trimmed };
      }
      if (isAuthenticatedWebviewHostname(parsed.hostname)) {
        return {
          type: "deeplink",
          url: buildAuthenticatedUrlDeeplink(trimmed, {
            navContext: "push",
            titleSource: title === undefined ? "fromDocument" : "fromUrl",
            ...(title === undefined ? {} : { title }),
          }),
        };
      }
      if (isInternalHostname(parsed.hostname)) {
        return { type: "internal", path: toInternalPath(parsed) };
      }
      return { type: "external", url: trimmed };
    } catch {
      return { type: "external", url: trimmed };
    }
  }

  return { type: "external", url: trimmed };
};

export const followTreatmentActionLinkNavigation = (
  navigation: TreatmentActionLinkNavigation,
  navigate: NavigateFunction
): void => {
  switch (navigation.type) {
    case "internal":
      navigate(navigation.path);
      break;
    case "deeplink":
    case "external":
      openDeeplink(navigation.url);
      break;
  }
};
